import React, { useEffect, useMemo, useState } from "react";
import Header from "@features/ux/components/Header";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useRunHistory, useCancelScheduledNotification } from "../hooks";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { Eye, FilterX, X } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@shared/ui/select";
import { Button } from "@shared/ui/button";
import { useNotificationChoices } from "@features/ux/hooks";
import { RunHistoryResult } from "../types";
import { useToast } from "@shared/ui/use-toast";

const getBadgeClass = (type: "status" | "channel" | "type", value: string) => {
  const baseClasses = {
    default: "bg-muted text-muted-foreground",
    // Channel types
    email: "bg-blue-50 text-blue-600",
    sms: "bg-green-50 text-green-600",
    push: "bg-purple-50 text-purple-600",
    // Status types
    completed: "bg-green-50 text-green-600",
    scheduled: "bg-blue-50 text-blue-600",
    failed: "bg-red-50 text-red-600",
    pending: "bg-yellow-50 text-yellow-600",
    // Type of
    bulk: "bg-gray-900 text-white",
    individual: "bg-blue-50 text-blue-600",
  };
  return baseClasses[value?.toLowerCase()] || baseClasses.default;
};

const getInitialFilters = (params: URLSearchParams) => ({
  type: params.get("type") || "",
  status: params.get("status") || "",
  order: params.get("order") || "",
});

// Helper function to format recipient info
const formatRecipientInfo = (
  recipientName: string,
  recipientsCount: number
) => {
  if (!recipientName) return "NA";

  if (recipientsCount <= 1) {
    return recipientName;
  }

  return `${recipientName} (${recipientsCount} recipients)`;
};

// Custom recipient cell component
const RecipientCell = ({ recipientName, recipientsCount }) => {
  const displayText = formatRecipientInfo(recipientName, recipientsCount);

  return <span title={displayText}>{displayText}</span>;
};

// Custom type cell component with icon
const TypeCell = ({ typeOf }) => {
  return (
    <Badge className={getBadgeClass("type", typeOf)}>
      <span className="mr-1">
        {typeOf?.toLowerCase() === "bulk" ? "📧" : "👤"}
      </span>
      {typeOf?.toUpperCase()}
    </Badge>
  );
};

const History = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  const [pendingFilters, setPendingFilters] = useState(
    getInitialFilters(searchParams)
  );
  const search = searchParams.get("search") || "";
  const appliedFilters = getInitialFilters(searchParams);

  // Hooks for choices (you might need to add these to your choices API)
  const { data: typeChoices } = useNotificationChoices("type_of");
  const { data: statusChoices } = useNotificationChoices("run_status");

  const {
    data: runHistory,
    isLoading,
    refetch,
  } = useRunHistory({
    remoteUtilityId,
    type: appliedFilters.type,
    status: appliedFilters.status,
    date: appliedFilters.order,
    search,
    page: currentPage,
  });

  const cancelMutation = useCancelScheduledNotification();

  const processedData = useMemo(() => {
    return (
      runHistory?.results.map((item) => ({
        ...item,
        id: item?.id,
        message: item?.templateName || "No template",
        recipientName: item?.recipientName,
        recipientsCount: item?.recipientsCount,
        channel: item?.channel || "N/A",
        status: item?.statusDisplay,
        scheduled:
          item?.statusDisplay?.toLowerCase() === "scheduled"
            ? `${item?.completeDate} ${item?.completedAt}`
            : "-",
        sent:
          item?.statusDisplay?.toLowerCase() !== "scheduled"
            ? `${item?.completeDate} ${item?.completedAt}`
            : "-",
        typeOf: item?.typeOf,
      })) ?? []
    );
  }, [runHistory]);

  const handleCancelNotification = async (notificationId: number) => {
    if (
      window.confirm(
        "Are you sure you want to cancel this scheduled notification?"
      )
    ) {
      try {
        await cancelMutation.mutateAsync(notificationId);
        toast({
          title: "Success",
          description: "Notification cancelled successfully",
        });
        refetch(); // Refresh the table
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to cancel notification",
          variant: "destructive",
        });
      }
    }
  };

  const handleViewNotification = (notificationId: number) => {
    // Navigate to the new page for viewing notification details
    navigate(`/communication-hub/scheduled/${notificationId}`);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "typeOf",
      header: "TYPE",
      type: "custom",
      render: (typeOf) => <TypeCell typeOf={typeOf} />,
    },
    {
      key: "message",
      header: "MESSAGE",
    },
    {
      key: "recipientName",
      header: "RECIPIENT",
      type: "custom",
      render: (_, row) => (
        <RecipientCell
          recipientName={row.recipientName}
          recipientsCount={row.recipientsCount}
        />
      ),
    },
    {
      key: "channel",
      header: "CHANNEL",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => getBadgeClass("channel", value),
      },
    },
    {
      key: "status",
      header: "STATUS",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => getBadgeClass("status", value),
      },
    },
    {
      key: "sent",
      header: "SENT",
    },
  ];

  const handleApplyFilters = () => {
    const newParams: Record<string, string> = {
      ...(pendingFilters.type && { type: pendingFilters.type }),
      ...(pendingFilters.status && { status: pendingFilters.status }),
      ...(pendingFilters.order && { order: pendingFilters.order }),
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    setPendingFilters({ type: "", status: "", order: "" });
    const newParams = Object.fromEntries(searchParams.entries());
    ["type", "status", "order", "search", "page"].forEach(
      (key) => delete newParams[key]
    );
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams = {
      ...Object.fromEntries(searchParams.entries()),
      search: searchTerm,
    };
    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  useEffect(() => {
    setPendingFilters(getInitialFilters(searchParams));
  }, [searchParams]);

  const pagination = useMemo(() => {
    const count = runHistory?.count || 0;
    const itemsPerPage = 10; // Adjust based on your API response
    const totalPages = Math.ceil(count / itemsPerPage);
    return runHistory
      ? {
          currentPage,
          totalPages,
          totalItems: count,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, count),
          itemsPerPage,
        }
      : undefined;
  }, [runHistory, currentPage]);

  const renderSelectFilter = (
    label: string,
    field: keyof typeof pendingFilters,
    options: { key: string; value: string }[],
    placeholder = `Select ${label}`
  ) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <Select
        value={pendingFilters[field]}
        onValueChange={(value) =>
          setPendingFilters((prev) => ({ ...prev, [field]: value }))
        }
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.key} value={String(opt.key)}>
              {opt.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const filterPanel = useMemo(
    () => (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderSelectFilter(
            "Type",
            "type",
            typeChoices || [
              { key: "bulk", value: "Bulk" },
              { key: "individual", value: "Individual" },
            ]
          )}
          {renderSelectFilter(
            "Status",
            "status",
            statusChoices || [
              { key: "completed", value: "Completed" },
              { key: "scheduled", value: "Scheduled" },
              { key: "failed", value: "Failed" },
            ]
          )}
          {renderSelectFilter("Sort By", "order", [
            { key: "-complete_date", value: "Newest" },
            { key: "complete_date", value: "Oldest" },
          ])}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="default" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
          <Button variant="ghost" onClick={handleResetFilters}>
            <FilterX className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>
    ),
    [pendingFilters, typeChoices, statusChoices]
  );

  const hasActiveFilters =
    pendingFilters.type || pendingFilters.status || pendingFilters.order;

  return (
    <div className="space-y-4">
      <Header
        title="Run History"
        subtext="View and manage your notification run history, track delivery status, and cancel scheduled notifications."
      />
      <GenericTable
        data={processedData}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by template name or recipient"
        emptyMessage="No run history found"
      />
    </div>
  );
};

export default History;
